import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Virtuoso } from 'react-virtuoso';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { Content, useContent } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import * as Tabs from 'src/core/ui/Tabs';
import { useEventMarketplaceSettings } from 'src/hooks/useEventMarketplaceSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { GroupsPreview } from 'src/modals/GroupListingsV2/components/GroupsPreview';
import { ContentId } from 'src/utils/constants/contentId';
import { newGuidId } from 'src/utils/idUtils';
import { EventWithData, Feature, Listing } from 'src/WebApiController';
import { Event, MergeListingGroupInput } from 'src/WebApiController';

import IconBulletList from '~icons/pos/bulletList';

import * as styles from '../GroupListings.css';
import { GroupingTemplates } from './GroupingTemplates';
import { MergeListingGroupInputListFields } from './groupingTypes';
import {
  flattenListingGroup,
  getListingsForMergeListingGroupInput,
} from './groupingUtils';
import { ListingGroupInput } from './ListingGroupInput';
import { ListingTableForGroups } from './ListingTableForGroups';

const TemplateTabKey = 'Template_Tab';

export const EventGroupings = ({
  events,
  onEventSelected,
  containerHeight,
}: {
  events: EventWithData[];
  onEventSelected: (event?: EventWithData) => void;
  containerHeight?: number;
}) => {
  const { watch, formState, setValue, getValues } =
    useFormContext<MergeListingGroupInputListFields>();

  const mergeListingGroupInputs = watch('mergeListingGroupInputs');
  const newGroupName = useContent(ContentId.NewGroup);

  const { defaultListingMarketplaceSettings } = useEventMarketplaceSettings(
    events?.[0]?.event
  );

  const onAddNewGroup = useCallback(
    (event: Event, eventIndex: number) => {
      mergeListingGroupInputs.push({
        listingGroupId: newGuidId(),
        name: `${newGroupName} ${mergeListingGroupInputs.length + 1}`,
        desiredActiveListings: getValues(
          `eventSettings.${eventIndex}.desiredActiveListings`
        ),
        minActiveListings: getValues(
          `eventSettings.${eventIndex}.minActiveListings`
        ),
        maxActiveListings: getValues(
          `eventSettings.${eventIndex}.maxActiveListings`
        ),
        targetPercentage: getValues(
          `eventSettings.${eventIndex}.targetPercentage`
        ),
        deprioritizedQuantities: getValues(
          `eventSettings.${eventIndex}.deprioritizedQuantities`
        ),
        marketplaceSettings: {
          listingMarketplaceSettings: defaultListingMarketplaceSettings,
        },
        viagogoEventId: event.viagId,
        viagogoMappingId: event.mpId,
        listingGroupItems: [],
        groupUndercutSetting: {
          undAbsAmt: null,
          undRelAmt: null,
          actRankUndAbsAmt: null,
          actRankUndRelAmt: null,
        },
        groupingMethods: {
          groupingMethods: [],
        },
        inheritListingGroupSettings: false,
        maintainPriceStagger: false,
      } as MergeListingGroupInput);

      setValue('mergeListingGroupInputs', [...mergeListingGroupInputs]);
    },
    [
      defaultListingMarketplaceSettings,
      getValues,
      mergeListingGroupInputs,
      newGroupName,
      setValue,
    ]
  );

  if (!events.length) {
    return (
      <EmptySectionContent.Root icon={<IconBulletList />}>
        <Content id={ContentId.NoGroupingAvailableForDefault} />
      </EmptySectionContent.Root>
    );
  }

  return (
    <Tabs.Root
      defaultValue={events.length > 1 ? TemplateTabKey : events[0].event.virtId}
    >
      {events.length > 1 && (
        <>
          <Tabs.List
            style={{
              // We need to override this and add the border to tabListContainer so it spans left-to-right
              // otherwise the width fit-content won't work
              border: 'none',
              marginBottom: 0,
              width: 'fit-content',
            }}
          >
            <Tabs.Trigger
              key={TemplateTabKey}
              value={TemplateTabKey}
              className={styles.tabsTrigger}
              onClick={() => onEventSelected(undefined)}
            >
              <Content id={ContentId.GroupingTemplate} />
            </Tabs.Trigger>
            {events.map((ev, i) => {
              let hasErrors = false;
              mergeListingGroupInputs.forEach((m, i) => {
                const isForThisEvent =
                  ev.event.virtId ===
                  (m.viagogoEventId?.toString() ?? m.viagogoMappingId);

                if (isForThisEvent) {
                  hasErrors =
                    hasErrors ||
                    Boolean(
                      formState.errors.mergeListingGroupInputs?.[i]?.name
                        ?.message
                    );
                }
              });

              return (
                <Tabs.Trigger
                  key={ev.event.virtId}
                  className={styles.tabsTrigger}
                  hasErrors={hasErrors}
                  value={ev.event.virtId}
                  onClick={() => onEventSelected(ev)}
                >
                  {i + 1}
                </Tabs.Trigger>
              );
            })}
          </Tabs.List>
          <Tabs.Content
            key={TemplateTabKey}
            value={TemplateTabKey}
            style={{
              height: containerHeight
                ? `calc(${containerHeight}px - ${vars.spacing['3xl']} - ${vars.spacing['5xl']})`
                : '75vh',
            }}
          >
            <GroupingTemplates events={events} />
          </Tabs.Content>
        </>
      )}

      {events.map((ev, i) => {
        const groupingInputs = mergeListingGroupInputs.filter(
          (m) =>
            ev.event.virtId ===
            (m.viagogoEventId?.toString() ?? m.viagogoMappingId)
        );

        const eventListings =
          ev.entities.listings
            ?.flatMap(flattenListingGroup)
            .map((l) => l as Listing) ?? [];

        return (
          <Stack
            key={ev.event.virtId}
            direction="column"
            gap="s"
            width="full"
            height="full"
          >
            <div className={styles.groupToolbar}>
              <Button onClick={() => onAddNewGroup(ev.event, i)} variant="text">
                + <Content id={ContentId.AddGroup} />
              </Button>
            </div>
            <GroupsPreview
              groups={groupingInputs}
              eventListings={eventListings}
            />
          </Stack>
        );
      })}
    </Tabs.Root>
  );
};
